import pkdApi from 'store/api/pkd';
import { setParticipantTasksSetupFailed, setSettingUpParticipantTasks } from 'store/application';
import { TaskTitles } from 'utils/enum';
import { filterParticipantTask } from 'utils/participantTask';

const handleProfileParticipantTasks = ({ data }, _, args = {}) => {
  const taskData = data?.map((data) => data?.data);

  const participantTasks = taskData?.filter(
    (task) => task.title !== TaskTitles.dailySurvey
  );

  return participantTasks
    .sort(
      ({ startDate: lhsStartDate }, { startDate: rhsStartDate }) =>
        new Date(rhsStartDate) - new Date(lhsStartDate)
    )
    .filter(
      ({ taskId }, index, array) =>
        array.findIndex((item) => taskId === item.taskId) === index
    )
    .filter(({ taskId }) => taskId !== 1)
    .sort(({ status: lhsStatus }, { status: rhsStatus }) =>
      // Keep completed at end
      lhsStatus === rhsStatus ? 0 : lhsStatus === 'completed' ? 1 : -1
    );
};

const handleParticipantTask = ({ data, included }) => {
  const taskEntries = included?.participantTaskEntries;
  const task = {
    ...(data?.attributes?.task ?? {}),
    ...(data?.attributes?.participantTask ?? {}),
  };

  return {
    ...data?.attributes,
    taskEntries,
    task,
  };
};

const handleProfileParticipantTask = ({ data, included }) => {
  const taskVersion = included?.['task-version'];
  const lastParticipantTaskEntry = included?.['last-participant-task-entry'];
  const lastCompletedParticipantTask = included?.['last-completed-participant-task'];
  const participantTask = data?.attributes?.['participant-task'];
  const taskDetails = data?.attributes?.['task'];

  return {
    formId            : taskVersion?.id,
    attributes        : data?.attributes?.task?.attributes,
    form              : taskVersion?.instrument,
    participantTaskId : data?.id,
    taskEntryId       : lastParticipantTaskEntry?.id,
    taskEntry         : lastParticipantTaskEntry?.data ?? lastCompletedParticipantTask?.latest_participant_task_entry?.data,
    taskTitle         : taskDetails?.title,
    task              : participantTask,
  };
};

const participantTaskApi = pkdApi.injectEndpoints({
  endpoints : (build) => ({
    getParticipantTask : build.query({
      query : ({ personAccountProfileId, participantTaskId }) => ({
        url    : `../v3/app/profiles/${personAccountProfileId}/participant-tasks/${participantTaskId}?include=task-version,task,last-participant-task-entry,participant-task-entries`,
        method : 'GET',
      }),
      providesTags : (result) =>
        result
          ? [{ type: 'ParticipantTasks', id: result?.task?.title }]
          : ['ParticipantTasks'],
      transformResponse : handleParticipantTask,
    }),
    getParticipantTasks : build.query({
      query : ({ personId, params }) => ({
        url    : `../v1/persons/${personId}/participant-tasks`,
        method : 'GET',
        params,
      }),
      providesTags : (result) =>
        result
          ? [
            ...result.map(({ task }) => ({
              type : 'ParticipantTasks',
              id   : task.title,
            })),
          ]
          : ['ParticipantTasks'],
      transformResponse : ({ data }, _, args = {}) => {
        const participantTasks = data?.filter?.((task) =>
          filterParticipantTask(data, task)
        );

        return participantTasks
          .sort((a, b) => a.sequence - b.sequence)
          .sort(({ status: lhsStatus }, { status: rhsStatus }) =>
            // Keep completed at end
            lhsStatus === rhsStatus ? 0 : lhsStatus === 'completed' ? 1 : -1
          )
          .filter(({ task }) => !args?.title || task?.title === args?.title)
          .filter(({ task }) => task?.title !== 'Daily Survey');
      },
    }),
    createParticipantTaskEntry : build.mutation({
      query : ({ participantTask, data, status }) => ({
        url    : `../v1/persons/${participantTask.personId}/participant-tasks/${participantTask.id}`,
        method : 'POST',
        body   : {
          data,
          status,
        },
        bypassCaseConverter : true,
      }),
      invalidatesTags : (_, __, { participantTask }) => [
        { type: 'ParticipantTasks', id: participantTask?.task?.title },
      ],
    }),
    updateParticipantTaskEntry : build.mutation({
      query : ({
        participantTaskId,
        personId,
        entries,
        status,
        participantTaskEntryId,
      }) => ({
        url    : `../v1/persons/${personId}/participant-tasks/${participantTaskId}`,
        method : 'PATCH',
        body   : {
          data : {
            attributes : {
              person_id : personId,
            },
          },
          status,
          entries,
          participant_task_entry_id : participantTaskEntryId,
        },
        bypassCaseConverter : true,
      }),
      invalidatesTags : (_, __, { participantTask }) => [
        { type: 'ParticipantTasks', id: participantTask?.task?.title },
      ],
    }),
    updateParticipantTask : build.mutation({
      query : ({ participantTaskId, entry, status }) => ({
        url    : `../v3/participant-tasks/${participantTaskId}`,
        method : 'PATCH',
        body   : {
          data : {
            type       : 'participant-tasks',
            id         : participantTaskId?.toString(),
            attributes : {
              status : status,
              entry,
            },
          },
        },
        headers : {
          'Content-Type' : 'application/vnd.api+json',
          Accept         : 'application/vnd.api+json',
        },
        bypassCaseConverter : true,
      }),
      invalidatesTags : (_, __, { participantTask }) => [
        { type: 'ParticipantTasks', id: participantTask?.task?.title },
        'DailySurveys',
        'DailySurveysCompleted',
        'ProfileParticipantTask',
      ],
    }),
    getParticipantTaskEntry : build.query({
      query : ({ personId, participantTaskId }) => ({
        url                 : `../v1/persons/${personId}/participant-tasks/${participantTaskId}`,
        method              : 'GET',
        bypassCaseConverter : true,
      }),
      providesTags : (result) =>
        result
          ? [{ type: 'ParticipantTasks', id: result.title }]
          : ['ParticipantTasks'],
      transformResponse : ({ data }) => ({
        title : data?.task?.title,
        ...data?.participant_task_entry,
      }),
    }),
    getProfileParticipantTasks : build.query({
      queryFn : async ({ personAccountProfileId, params }, api, extraOptions, baseQuery) => {
        // Retry logic to handle backend eventual consistency
        const maxRetries = 3;
        const retryDelay = 15000; // 15 seconds

        api.dispatch(setParticipantTasksSetupFailed(false));

        try {
          for (let attempt = 0; attempt <= maxRetries; attempt++) {
            const result = await baseQuery({
              url    : `../v3/app/profiles/${personAccountProfileId}/participant-tasks?include=last-participant-task-entry,oldest-participant-task-entry`,
              method : 'GET',
              params,
            });

            if (result.error) {
              return result;
            }

            // Apply transformResponse manually (ignored when using queryFn)
            const transformed = handleProfileParticipantTasks(result.data);

            // If successful and has data, return immediately
            if (transformed && transformed.length > 0) {
              return { data: transformed };
            }

            // On last attempt, return as is
            if (attempt === maxRetries) {
              if (!transformed?.length && !params?.status) {
                api.dispatch(setParticipantTasksSetupFailed(true));
              }

              return { data: transformed };
            }

            api.dispatch(setSettingUpParticipantTasks(true));

            // Wait 15 seconds before retrying
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        } finally {
          api.dispatch(setSettingUpParticipantTasks(false));
        }
      },
      providesTags : (result) =>
        result
          ? [{ type: 'ProfileParticipantTasks', id: result?.task?.title }]
          : ['ProfileParticipantTasks'],
      refetchOnMountOrArgChange : true,
    }),
    getProfileParticipantTask : build.query({
      query : ({ personAccountProfileId, participantTaskId }) => ({
        url                 : `../v3/app/profiles/${personAccountProfileId}/participant-tasks/${participantTaskId}?include=task-version,last-participant-task-entry,last-completed-participant-task`,
        method              : 'GET',
        bypassCaseConverter : true,
      }),
      providesTags : (result) =>
        result
          ? [{ type: 'ProfileParticipantTask', id: result?.task?.title }]
          : ['ProfileParticipantTask'],
      transformResponse : handleProfileParticipantTask,
    }),
  }),
  overrideExisting : false,
});

export const {
  useGetParticipantTaskQuery,
  useGetParticipantTasksQuery,
  useCreateParticipantTaskEntryMutation,
  useUpdateParticipantTaskEntryMutation,
  useUpdateParticipantTaskMutation,
  useGetParticipantTaskEntryQuery,
  useGetProfileParticipantTasksQuery,
  useGetProfileParticipantTaskQuery,
} = participantTaskApi;
